import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listSkusForScope, upsertSku, toggleSkuActive, importSkusCsv,
  listKitComponents, addKitComponent, removeKitComponent,
} from "@/lib/inventory-catalog.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { listSuppliers } from "@/lib/suppliers.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Loader2, Pencil, Upload, Package, History, Settings2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CsvImportDialog } from "@/components/csv-import-dialog";
import { PageHeader } from "@/components/ui/page-header";
import { ExportMenu } from "@/components/ui/export-menu";
import { FilterBar } from "@/components/ui/filter-bar";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { StatusPill } from "@/components/ui/status-pill";
import { CustodyTimeline } from "@/components/inventory/custody-timeline";
import { gToOz, ozToG, mmToIn, inToMm } from "@/lib/units";
import {
  useSkuPlanningSummary, RiskCell, DaysOfSupplyCell, WeeklyVelocityCell,
  ProjectedStockoutCell, OpenRecommendationCell,
} from "@/components/inventory/planning-cells";

export const Route = createFileRoute("/_authenticated/app/inventory/skus")({ component: SkusPage });

type Sku = {
  id: string; sku_code: string; name: string; barcode: string | null;
  active: boolean; customer_id: string; reorder_point: number;
  track_serials: boolean; track_kit: boolean; is_bundle: boolean; uom: string;
  weight_grams: number | null; length_mm: number | null; width_mm: number | null; height_mm: number | null;
  kitting_cost_cents: number | null;
  category: string | null; country_of_origin: string | null; hs_tariff_code: string | null;
  supplier_id: string | null; lead_time_days: number | null;
};

type FormState = {
  sku_code: string; name: string; barcode: string; reorder_point: string;
  track_serials: boolean; track_kit: boolean; is_bundle: boolean; uom: string; weight_grams: string;
  length_mm: string; width_mm: string; height_mm: string; active: boolean;
  kitting_cost: string;
  category: string; country_of_origin: string; hs_tariff_code: string;
  supplier_id: string; lead_time_days: string;
};
const empty: FormState = { sku_code: "", name: "", barcode: "", reorder_point: "0", track_serials: false, track_kit: false, is_bundle: false, uom: "each", weight_grams: "", length_mm: "", width_mm: "", height_mm: "", active: true, kitting_cost: "", category: "", country_of_origin: "", hs_tariff_code: "", supplier_id: "", lead_time_days: "" };

function SkusPage() {
  const qc = useQueryClient();
  const { tenantId, customerId, customers, facilityId } = useScope();
  const listSkusFn = useServerFn(listSkusForScope);
  const upsertSkuFn = useServerFn(upsertSku);
  const toggleActiveFn = useServerFn(toggleSkuActive);
  const importSkusFn = useServerFn(importSkusCsv);
  const listSuppliersFn = useServerFn(listSuppliers);
  const tenantCustomers = useMemo(() => customers.filter((c) => c.tenant_id === tenantId), [customers, tenantId]);

  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Sku | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [formCustomer, setFormCustomer] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [custodyFor, setCustodyFor] = useState<Sku | null>(null);
  const [bomFor, setBomFor] = useState<Sku | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["skus", tenantId, customerId],
    enabled: !!tenantId,
    queryFn: async () =>
      (await listSkusFn({ data: { tenantId: tenantId!, customerId: customerId ?? null } })) as Sku[],
    placeholderData: keepPreviousData,
  });

  const { data: suppliersResp } = useQuery({
    queryKey: ["suppliers-for-skus", tenantId],
    enabled: !!tenantId,
    queryFn: async () => await listSuppliersFn({ data: { tenantId: tenantId!, includeInactive: false } }),
  });
  const suppliers = (suppliersResp?.rows ?? []) as Array<{ id: string; name: string; default_lead_time_days: number }>;
  const supplierById = useMemo(() => new Map(suppliers.map((s) => [s.id, s])), [suppliers]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    return data.filter((r) => {
      if (activeFilter === "active" && !r.active) return false;
      if (activeFilter === "inactive" && r.active) return false;
      if (!s) return true;
      return r.sku_code.toLowerCase().includes(s)
        || r.name.toLowerCase().includes(s)
        || (r.barcode ?? "").toLowerCase().includes(s);
    });
  }, [data, search, activeFilter]);

  const skuIdsOnPage = useMemo(() => filtered.map((s) => s.id), [filtered]);
  const planningMap = useSkuPlanningSummary({ tenantId, facilityId, skuIds: skuIdsOnPage });

  const customerName = (id: string) => customers.find((c) => c.id === id)?.name ?? "—";

  const openCreate = () => { setEditing(null); setForm(empty); setFormCustomer(""); setFormOpen(true); };
  const openEdit = (s: Sku) => {
    setEditing(s);
    setForm({
      sku_code: s.sku_code, name: s.name, barcode: s.barcode ?? "", reorder_point: String(s.reorder_point ?? 0),
      track_serials: s.track_serials, track_kit: s.track_kit, is_bundle: s.is_bundle ?? false, uom: s.uom ?? "each",
      weight_grams: gToOz(s.weight_grams),
      length_mm: mmToIn(s.length_mm),
      width_mm: mmToIn(s.width_mm),
      height_mm: mmToIn(s.height_mm),
      active: s.active,
      kitting_cost: s.kitting_cost_cents != null ? (s.kitting_cost_cents / 100).toFixed(2) : "",
      category: s.category ?? "",
      country_of_origin: s.country_of_origin ?? "",
      hs_tariff_code: s.hs_tariff_code ?? "",
      supplier_id: s.supplier_id ?? "",
      lead_time_days: s.lead_time_days != null ? String(s.lead_time_days) : "",
    });
    setFormOpen(true);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    const payload = {
      sku_code: form.sku_code.trim(),
      name: form.name.trim(),
      barcode: form.barcode.trim() || null,
      reorder_point: parseInt(form.reorder_point, 10) || 0,
      track_serials: form.track_serials,
      track_kit: form.track_kit,
      is_bundle: form.is_bundle,
      uom: form.uom.trim() || "each",
      weight_grams: ozToG(form.weight_grams),
      length_mm: inToMm(form.length_mm),
      width_mm: inToMm(form.width_mm),
      height_mm: inToMm(form.height_mm),
      active: form.active,
      kitting_cost_cents: form.track_kit && form.kitting_cost.trim() !== ""
        ? Math.max(0, Math.round(parseFloat(form.kitting_cost) * 100))
        : 0,
      category: form.category.trim() || null,
      country_of_origin: form.country_of_origin.trim().toUpperCase() || null,
      hs_tariff_code: form.hs_tariff_code.trim() || null,
      supplier_id: form.supplier_id || null,
      lead_time_days: form.lead_time_days.trim() === "" ? null : Math.max(0, parseInt(form.lead_time_days, 10) || 0),
    };
    if (form.track_kit && form.is_bundle) {
      return toast.error("A SKU cannot be both a kit and a bundle");
    }
    setSubmitting(true);
    const cid = editing ? undefined : (customerId ?? formCustomer);
    if (!editing && !cid) { setSubmitting(false); return toast.error("Pick a customer"); }
    try {
      await runServerFn(
        () => upsertSkuFn({ data: { id: editing?.id, tenantId, customerId: cid, payload } }),
        editing ? "Update SKU" : "Create SKU",
      );
      toast.success(editing ? "SKU updated" : "SKU created");
      setFormOpen(false);
      qc.invalidateQueries({ queryKey: ["skus"] });
    } catch { /* toast handled */ } finally { setSubmitting(false); }
  };

  const toggleActive = async (s: Sku) => {
    if (!tenantId) return;
    try {
      await runServerFn(
        () => toggleActiveFn({ data: { id: s.id, tenantId, active: !s.active } }),
        "Toggle SKU",
      );
      toast.success(s.active ? "SKU deactivated" : "SKU activated");
      qc.invalidateQueries({ queryKey: ["skus"] });
    } catch { /* toast handled */ }
  };

  const importCustomer = customerId ?? "";

  const cols: ColumnDef<Sku, unknown>[] = [
    { id: "code", header: "Code", accessorKey: "sku_code", cell: ({ row }) => <span className="font-mono text-sm">{row.original.sku_code}</span> },
    { id: "name", header: "Name", accessorKey: "name", cell: ({ row }) => <span className="font-medium">{row.original.name}</span> },
    { id: "customer", header: "Customer", accessorFn: (r) => customerName(r.customer_id), cell: ({ getValue }) => <span className="text-muted-foreground text-sm">{String(getValue() ?? "—")}</span> },
    { id: "barcode", header: "Barcode", cell: ({ row }) => <span className="font-mono text-xs text-muted-foreground">{row.original.barcode ?? "—"}</span> },
    { id: "category", header: "Category", accessorKey: "category", cell: ({ row }) => <span className="text-sm text-muted-foreground">{row.original.category ?? "—"}</span> },
    { id: "origin", header: "Origin", accessorKey: "country_of_origin", cell: ({ row }) => <span className="font-mono text-xs text-muted-foreground">{row.original.country_of_origin ?? "—"}</span> },
    { id: "hs", header: "HS Code", accessorKey: "hs_tariff_code", cell: ({ row }) => <span className="font-mono text-xs text-muted-foreground">{row.original.hs_tariff_code ?? "—"}</span> },
    { id: "rp", header: "Reorder pt", accessorKey: "reorder_point", cell: ({ row }) => <span className="tabular-nums text-sm">{row.original.reorder_point ?? 0}</span> },
    { id: "dos", header: "DoS", enableSorting: false,
      cell: ({ row }) => <DaysOfSupplyCell skuId={row.original.id} summary={planningMap.get(row.original.id)} /> },
    { id: "vel", header: "Weekly", enableSorting: false,
      cell: ({ row }) => <WeeklyVelocityCell summary={planningMap.get(row.original.id)} /> },
    { id: "stockout", header: "Stockout", enableSorting: false,
      cell: ({ row }) => <ProjectedStockoutCell summary={planningMap.get(row.original.id)} /> },
    { id: "rec", header: "Open rec", enableSorting: false,
      cell: ({ row }) => <OpenRecommendationCell skuId={row.original.id} summary={planningMap.get(row.original.id)} /> },
    { id: "risk", header: "Risk", enableSorting: false,
      cell: ({ row }) => <RiskCell skuId={row.original.id} summary={planningMap.get(row.original.id)} /> },
    { id: "serials", header: "Serials", cell: ({ row }) => row.original.track_serials ? <StatusPill tone="info">Tracked</StatusPill> : <span className="text-muted-foreground text-xs">—</span> },
    { id: "status", header: "Status", cell: ({ row }) => (
      <button onClick={(e) => { e.stopPropagation(); toggleActive(row.original); }} className="cursor-pointer">
        <StatusPill tone={row.original.active ? "success" : "neutral"} dot>{row.original.active ? "Active" : "Inactive"}</StatusPill>
      </button>
    ) },
    { id: "actions", header: "", enableSorting: false, cell: ({ row }) => (
      <div className="flex items-center justify-end gap-1">
        <Button size="sm" variant="ghost" title="Custody timeline" onClick={(e) => { e.stopPropagation(); setCustodyFor(row.original); }}><History className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" title="Edit" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><Pencil className="h-4 w-4" /></Button>
      </div>
    ) },
  ];

  return (
    <>
      <PageHeader
        title="SKUs"
        description="Per-customer product catalog."
        actions={
          <>
            <CsvImportDialog
              trigger={<Button size="sm" variant="outline" disabled={!importCustomer}><Upload className="h-4 w-4 mr-2" />Import CSV</Button>}
              title="Import SKUs"
              templateHeaders={["sku_code", "name", "barcode", "reorder_point", "track_serials", "uom", "weight_oz", "category", "country_of_origin", "hs_tariff_code"]}
              sampleRow={["SKU-001", "Sample widget", "0123456789012", "10", "false", "each", "8.82", "Hoodies", "US", "6110.20.20"]}
              validateRow={(row) => {
                const sku_code = (row.sku_code ?? "").trim();
                const name = (row.name ?? "").trim();
                if (!sku_code) return { ok: false, error: "sku_code is required" };
                if (!name) return { ok: false, error: "name is required" };
                return { ok: true, value: {
                  sku_code,
                  name,
                  barcode: (row.barcode ?? "").trim() || null,
                  reorder_point: parseInt(row.reorder_point ?? "0", 10) || 0,
                  track_serials: ["true", "1", "yes"].includes((row.track_serials ?? "").toLowerCase()),
                  uom: (row.uom ?? "each").trim() || "each",
                  weight_grams: ozToG(row.weight_oz),
                  category: (row.category ?? "").trim() || null,
                  country_of_origin: (row.country_of_origin ?? "").trim().toUpperCase() || null,
                  hs_tariff_code: (row.hs_tariff_code ?? "").trim() || null,
                } };
              }}
              onImport={async (rows) => {
                try {
                  return await importSkusFn({ data: {
                    tenantId: tenantId!, customerId: importCustomer,
                    rows: rows as never,
                  } });
                } catch (e) {
                  return { inserted: 0, failed: [{ row: 0, error: (e as Error).message }] };
                }
              }}
              onDone={() => qc.invalidateQueries({ queryKey: ["skus"] })}
            />
            {tenantId ? (
              <ExportMenu
                entity="skus"
                tenantId={tenantId}
                customerId={customerId}
                getRows={() =>
                  filtered.map((r) => ({
                    sku_code: r.sku_code,
                    name: r.name,
                    barcode: r.barcode ?? "",
                    customer: customerName(r.customer_id),
                    uom: r.uom ?? "",
                    reorder_point: r.reorder_point ?? 0,
                    track_serials: r.track_serials,
                    is_bundle: r.is_bundle ?? false,
                    weight_grams: r.weight_grams ?? "",
                    category: r.category ?? "",
                    country_of_origin: r.country_of_origin ?? "",
                    hs_tariff_code: r.hs_tariff_code ?? "",
                    active: r.active,
                  }))
                }
                columns={[
                  "sku_code","name","barcode","customer","uom","reorder_point",
                  "track_serials","is_bundle","weight_grams","category",
                  "country_of_origin","hs_tariff_code","active",
                ]}
                filters={{ search, activeFilter }}
                disabled={filtered.length === 0}
              />
            ) : null}
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
              <DialogTrigger asChild><Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-2" />New SKU</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>{editing ? "Edit SKU" : "Create SKU"}</DialogTitle></DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4">
                  {!editing && !customerId && (
                    <div className="space-y-2"><Label>Customer</Label>
                      <Select value={formCustomer} onValueChange={setFormCustomer}>
                        <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                        <SelectContent>{tenantCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>SKU code</Label><Input required value={form.sku_code} onChange={(e) => setForm({ ...form, sku_code: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Barcode</Label><Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} /></div>
                  </div>
                  <div className="space-y-2"><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2"><Label>UOM</Label><Input value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Reorder pt</Label><Input type="number" min={0} value={form.reorder_point} onChange={(e) => setForm({ ...form, reorder_point: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Weight (oz)</Label><Input type="number" min={0} step="0.01" value={form.weight_grams} onChange={(e) => setForm({ ...form, weight_grams: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2"><Label>Length (in)</Label><Input type="number" min={0} step="0.01" value={form.length_mm} onChange={(e) => setForm({ ...form, length_mm: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Width (in)</Label><Input type="number" min={0} step="0.01" value={form.width_mm} onChange={(e) => setForm({ ...form, width_mm: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Height (in)</Label><Input type="number" min={0} step="0.01" value={form.height_mm} onChange={(e) => setForm({ ...form, height_mm: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2"><Label>Category</Label><Input placeholder="Hoodies" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Origin (ISO-2)</Label><Input maxLength={2} placeholder="US" value={form.country_of_origin} onChange={(e) => setForm({ ...form, country_of_origin: e.target.value.toUpperCase() })} /></div>
                    <div className="space-y-2"><Label>HS Tariff Code</Label><Input placeholder="6110.20.20" value={form.hs_tariff_code} onChange={(e) => setForm({ ...form, hs_tariff_code: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Supplier</Label>
                      <Select
                        value={form.supplier_id || "__none"}
                        onValueChange={(v) => {
                          const supplierId = v === "__none" ? "" : v;
                          const sup = supplierId ? supplierById.get(supplierId) : null;
                          // Default lead time from supplier when empty or when changing supplier on a fresh field
                          const shouldDefault = sup && (form.lead_time_days.trim() === "" || form.supplier_id !== supplierId);
                          setForm({
                            ...form,
                            supplier_id: supplierId,
                            lead_time_days: shouldDefault ? String(sup!.default_lead_time_days) : form.lead_time_days,
                          });
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">None</SelectItem>
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name} · {s.default_lead_time_days}d</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Lead time (days)</Label>
                      <Input
                        type="number"
                        min={0}
                        placeholder={form.supplier_id ? String(supplierById.get(form.supplier_id)?.default_lead_time_days ?? "") : "—"}
                        value={form.lead_time_days}
                        onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">
                        {form.supplier_id
                          ? "Defaults from supplier. Override to set a SKU-specific lead time."
                          : "Pick a supplier to default, or enter a SKU-specific lead time."}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-md border px-3 py-2">
                    <div><Label className="text-sm">Track serial numbers</Label><p className="text-xs text-muted-foreground">Require unique serials at receive and ship.</p></div>
                    <Switch checked={form.track_serials} onCheckedChange={(v) => setForm({ ...form, track_serials: v })} />
                  </div>
                  <div className="flex items-center justify-between rounded-md border px-3 py-2">
                    <div><Label className="text-sm">Kit (finished good)</Label><p className="text-xs text-muted-foreground">Produced via a work order. Ships only when kit inventory is on hand.</p></div>
                    <Switch checked={form.track_kit} onCheckedChange={(v) => setForm({ ...form, track_kit: v, is_bundle: v ? false : form.is_bundle })} />
                  </div>
                  <div className="flex items-center justify-between rounded-md border px-3 py-2">
                    <div><Label className="text-sm">Bundle (sold as group)</Label><p className="text-xs text-muted-foreground">Holds no inventory of its own. Orders are picked and shipped as the individual component SKUs.</p></div>
                    <Switch checked={form.is_bundle} onCheckedChange={(v) => setForm({ ...form, is_bundle: v, track_kit: v ? false : form.track_kit })} />
                  </div>
                  {editing && (form.track_kit || form.is_bundle) && (
                    <div className="flex items-center justify-between rounded-md border px-3 py-2">
                      <div><Label className="text-sm">Components</Label><p className="text-xs text-muted-foreground">Define the bill of materials for this {form.track_kit ? "kit" : "bundle"}.</p></div>
                      <Button type="button" size="sm" variant="outline" onClick={() => setBomFor(editing)}><Settings2 className="h-4 w-4 mr-1" /> Manage</Button>
                    </div>
                  )}
                  {form.track_kit && (
                    <div className="space-y-2">
                      <Label>Billable kitting cost ($ per kit)</Label>
                      <Input type="number" min={0} step="0.01" placeholder="0.00" value={form.kitting_cost} onChange={(e) => setForm({ ...form, kitting_cost: e.target.value })} />
                      <p className="text-xs text-muted-foreground">Charged per kit built on a work order.</p>
                    </div>
                  )}
                  {editing && (
                    <div className="flex items-center justify-between rounded-md border px-3 py-2">
                      <div><Label className="text-sm">Active</Label><p className="text-xs text-muted-foreground">Inactive SKUs are hidden from new orders.</p></div>
                      <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                    </div>
                  )}
                  <DialogFooter><Button type="submit" disabled={submitting}>{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editing ? "Save" : "Create"}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </>
        }
        meta={!importCustomer ? <span className="text-xs text-muted-foreground">Pick a customer in the scope bar to enable CSV import.</span> : undefined}
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <FilterBar search={search} onSearchChange={setSearch} searchPlaceholder="Search SKU code, name, or barcode…">
          <Select value={activeFilter} onValueChange={(v) => setActiveFilter(v as typeof activeFilter)}>
            <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </FilterBar>
        <DataTable
          data={filtered}
          columns={cols}
          isLoading={isLoading}
          getRowId={(r) => r.id}
          empty={{ title: data && data.length > 0 ? "No matches" : "No SKUs yet", description: data && data.length > 0 ? "Adjust your search or filter." : "Create a SKU or import a CSV to get started.", action: !data?.length ? <Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-2" />New SKU</Button> : undefined }}
          mobileCard={(s) => (
            <div className="space-y-1.5" onClick={() => openEdit(s)}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm flex items-center gap-1.5"><Package className="h-3 w-3 text-muted-foreground" />{s.sku_code}</span>
                <StatusPill tone={s.active ? "success" : "neutral"} dot>{s.active ? "Active" : "Inactive"}</StatusPill>
              </div>
              <div className="font-medium text-sm truncate">{s.name}</div>
              <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
                <span>{customerName(s.customer_id)}</span>
                {s.barcode && <span className="font-mono">{s.barcode}</span>}
                <span>RP: {s.reorder_point}</span>
                {s.track_serials && <span className="text-info">Serials</span>}
              </div>
            </div>
          )}
        />
      </div>
      <Dialog open={!!custodyFor} onOpenChange={(o) => { if (!o) setCustodyFor(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Custody timeline · <span className="font-mono">{custodyFor?.sku_code}</span>
            </DialogTitle>
          </DialogHeader>
          {custodyFor && tenantId && (
            <div className="max-h-[60vh] overflow-auto -mx-1 px-1">
              <CustodyTimeline tenantId={tenantId} entity="sku" id={custodyFor.id} limit={200} />
            </div>
          )}
        </DialogContent>
      </Dialog>
      {bomFor && tenantId && (
        <KitComponentsDialog
          kit={bomFor}
          tenantId={tenantId}
          allSkus={(data ?? []).filter((s) => s.customer_id === bomFor.customer_id)}
          onClose={() => { setBomFor(null); setFormOpen(true); }}
        />
      )}
    </>
  );
}

function KitComponentsDialog({ kit, tenantId, allSkus, onClose }: { kit: Sku; tenantId: string; allSkus: Sku[]; onClose: () => void }) {
  const qc = useQueryClient();
  const listFn = useServerFn(listKitComponents);
  const addFn = useServerFn(addKitComponent);
  const removeFn = useServerFn(removeKitComponent);
  const [componentSku, setComponentSku] = useState("");
  const [qty, setQty] = useState("1");

  const { data: comps = [] } = useQuery({
    queryKey: ["kit-components", kit.id],
    queryFn: () => listFn({ data: { kitId: kit.id } }),
  });

  const add = async () => {
    if (!componentSku) return;
    const n = parseInt(qty, 10);
    if (!n || n <= 0) return toast.error("Qty must be > 0");
    try {
      await runServerFn(
        () => addFn({ data: { tenantId, parentSkuId: kit.id, componentSkuId: componentSku, qtyPerKit: n } }),
        "Add component",
      );
      setComponentSku(""); setQty("1");
      qc.invalidateQueries({ queryKey: ["kit-components", kit.id] });
    } catch { /* toast handled */ }
  };
  const remove = async (id: string) => {
    try {
      await runServerFn(
        () => removeFn({ data: { id, tenantId, parentSkuId: kit.id } }),
        "Remove component",
      );
      qc.invalidateQueries({ queryKey: ["kit-components", kit.id] });
    } catch { /* toast handled */ }
  };

  const available = allSkus.filter((s) => s.id !== kit.id && !s.track_kit && !s.is_bundle && !comps.find((c: any) => c.component_sku_id === s.id));
  const parentLabel = kit.track_kit ? "Kit" : kit.is_bundle ? "Bundle" : "Components";
  const perLabel = kit.is_bundle ? "Qty / bundle" : "Qty / kit";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{parentLabel} components — <span className="font-mono">{kit.sku_code}</span> {kit.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr><th className="text-left p-2">Component</th><th className="text-left p-2 w-24">{perLabel}</th><th className="w-12"></th></tr></thead>
              <tbody>
                {comps.length === 0 && <tr><td colSpan={3} className="text-center text-muted-foreground py-4">No components yet.</td></tr>}
                {comps.map((c: any) => (
                  <tr key={c.id} className="border-t">
                    <td className="p-2"><span className="font-mono">{c.skus?.sku_code}</span> <span className="text-muted-foreground">{c.skus?.name}</span></td>
                    <td className="p-2">{c.qty_per_kit}</td>
                    <td className="p-2 text-right"><Button size="sm" variant="ghost" onClick={() => remove(c.id)}><Trash2 className="h-4 w-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2 items-end border-t pt-4">
            <div className="flex-1">
              <Label>Add component</Label>
              <Select value={componentSku} onValueChange={setComponentSku}>
                <SelectTrigger><SelectValue placeholder="Pick SKU" /></SelectTrigger>
                <SelectContent>{available.map((s) => <SelectItem key={s.id} value={s.id}>{s.sku_code} — {s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="w-24"><Label>Qty</Label><Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} /></div>
            <Button onClick={add}><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
